import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, Variants } from 'framer-motion'; // Import Variants type
import './Styles/LandingPage.css';
import logo from '../assets/logo.png'; // Make sure you have a logo at this path
import history from '../assets/history.png'; // Make sure you have a logo at this path
import Header from '../Components/Header';

// --- Sample Data for Carousel ---
// In a real app, this might come from an API
const sampleVideos = [
  { id: 1, title: 'Introduction to Business', thumbnail: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8YnVzaW5lc3N8fHx8fHwxNjE2NjYyMDIy&ixlib=rb-1.2.1&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=1080' },
  { id: 2, title: 'Digital Art Fundamentals', thumbnail: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8dGVjaG5vbG9neXx8fHx8fDE2MTY2NjIwNTU&ixlib=rb-1.2.1&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=1080' },
  { id: 3, title: 'Advanced Mathematics', thumbnail: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8c2NpZW5jZXx8fHx8fDE2MTY2NjIxMDU&ixlib=rb-1.2.1&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=1080' },
  { id: 4, title: 'History of Ethiopia', thumbnail: history }
];

// --- Animation Variants ---
// Explicitly type the constant with the Variants type from framer-motion
const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      {/* --- Header --- */}
      {/* <header className="landing-header">
        <div className="header-content">
          <img src={logo} alt="Hameruha Logo" className="logo" />
          <nav className="landing-nav">
            <button className="btn-nav" onClick={() => navigate('/login')}>Log In</button>
            <button className="btn-nav primary" onClick={() => navigate('/login')}>Sign Up</button>
          </nav>
        </div>
      </header> */}

      <Header></Header>

      <main>
        {/* --- Hero Section --- */}
        <section className="hero-section">
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              Unlock Your Potential, <br /> One Video at a Time.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="hero-subtitle"
            >
              Hameruha offers expert-led courses across diverse categories, available in multiple languages to fit your learning style.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <button className="btn-hero" onClick={() => navigate('/videos')}>Go to courses</button>
            </motion.div>
          </div>
        </section>

        {/* --- Language Showcase Section --- */}
        <motion.section
          className="language-showcase"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
        >
          <h3 className="section-tag">Learn in Your Language</h3>
          <div className="language-pills">
            <span>English</span>
            <span>አማርኛ</span>
            <span>Tigrigna</span>
            <span>Afaan Oromoo</span>
            <span>Soomaali</span>
            <span>Afar</span>
          </div>
        </motion.section>

        {/* --- How It Works Section --- */}
        <motion.section
          className="how-it-works"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <h2 className="section-title">Start Learning in 3 Easy Steps</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <h3 className="step-title">Create Account</h3>
              <p className="step-desc">Sign up for free and unlock access to our entire library.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3 className="step-title">Choose a Course</h3>
              <p className="step-desc">Browse categories and find the perfect course for your goals.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3 className="step-title">Start Watching</h3>
              <p className="step-desc">Learn at your own pace with high-quality, on-demand video lessons.</p>
            </div>
          </div>
        </motion.section>

        {/* --- Video Carousel Section --- */}
        <motion.section
          className="carousel-section"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <h2 className="section-title">Explore Our Popular Courses</h2>
          <p className="section-subtitle">Get a glimpse of what our students are learning right now.</p>
          <div className="carousel-container" onClick={() => navigate('/home')}>
            <div className="carousel-track">
              {sampleVideos.concat(sampleVideos).map((video, index) => ( // Duplicate for seamless scroll effect
                <div className="video-card" key={`${video.id}-${index}`}>
                  <img src={video.thumbnail} alt={video.title} className="video-thumbnail" />
                  <div className="video-overlay">
                    <h4 className="video-title">{video.title}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* --- Call to Action Section --- */}
        <motion.section
          className="cta-section"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
        >
          <h2 className="cta-title">Ready to Start Your Learning Journey?</h2>
          <p className="cta-subtitle">Join thousands of learners and gain new skills today.</p>
          <button className="btn-cta" onClick={() => navigate('/login')}>Sign Up Now</button>
        </motion.section>
      </main>

      {/* --- Footer --- */}
      <footer className="landing-footer">
        <p>&copy; {new Date().getFullYear()} Hameruha. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;